import {
	Button,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Paper,
	WithStyles,
	withStyles,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	TableFooter,
} from '@material-ui/core';
import moment from 'moment';
import {Component, ReactNode} from 'react';
import {RouteComponentProps} from 'react-router';

import {printHours} from 'Utilities';

import bookedTimesOverviewStyles from './BookedTimesOverviewStyles';
import {UserDataModel} from '../Admin/UserData.Model';
import TimeTrackingService from '../Common/TimeTrackingService';
import TimeTrackingMonth from '../Tracking/DataModels/TimeTrackingMonth';

interface State {
	selectedUser: UserDataModel | null;
	selectedMonth: number;
	selectedYear: number;
	selectableYears: number[];
	loading: boolean;
	timeTrackingMonths: TimeTrackingMonth[];
	refreshButtonClicked: boolean;
}

interface Props
	extends WithStyles<typeof bookedTimesOverviewStyles>,
		RouteComponentProps {}

class BookedTimesOverview extends Component<Props, State> {
	private readonly timeTrackingService: TimeTrackingService;

	public constructor(props: Props) {
		super(props);
		this.timeTrackingService = new TimeTrackingService();

		this.state = {
			selectedUser: null,
			selectedMonth: moment().month(),
			selectedYear: moment().year(),
			selectableYears: [],
			loading: false,
			timeTrackingMonths: [],
			refreshButtonClicked: false,
		};
	}

	public async componentDidMount(): Promise<void> {
		await this.loadSelectableYears();
	}

	private selectChanged(
		event: React.ChangeEvent<{name?: string; value: unknown}>,
		_: ReactNode,
		selectionType: string
	): void {
		if (selectionType === 'month') {
			this.setState({
				selectedMonth: parseInt(event.target.value as string, 10),
			});
		} else if (selectionType === 'year') {
			this.setState({
				selectedYear: parseInt(event.target.value as string, 10),
			});
		}
	}

	private async loadSelectableYears(): Promise<void> {
		const years: number[] = [];
		const timeTrackingMonths =
			await this.timeTrackingService.getAllTrackedTimes();

		for (const timeTrackingMonth of timeTrackingMonths) {
			if (!years.includes(timeTrackingMonth.year)) {
				years.push(timeTrackingMonth.year);
			}
		}

		this.setState({selectableYears: years});
	}

	private async loadTrackedTimes(): Promise<void> {
		this.setState({loading: true});
		const selectedMonth = moment();
		selectedMonth.set('year', this.state.selectedYear);
		selectedMonth.set('month', this.state.selectedMonth);
		const timeTrackingMonths =
			await this.timeTrackingService.getMultipleTrackedTimes(
				selectedMonth
			);
		this.setState({loading: false, timeTrackingMonths});
	}

	private loadEmployeesVacations(): void {
		this.loadTrackedTimes();
		this.setState({refreshButtonClicked: true});
	}

	public render(): ReactNode {
		return (
			<>
				<div className='bc-unprintable'>
					<div className={this.props.classes.headerContainer}>
						<h1>Gebuchte Stunden Übersicht</h1>
						<div className={this.props.classes.filterContainer}>
							<FormControl
								style={{width: '150px', marginRight: '16px'}}
								variant='outlined'
							>
								<InputLabel>Monat</InputLabel>
								<Select
									label='Monat'
									value={this.state.selectedMonth}
									onChange={(e, c): void => {
										this.selectChanged(e, c, 'month');
									}}
								>
									<MenuItem key='month_0' value={0}>
										Jänner
									</MenuItem>
									<MenuItem key='month_1' value={1}>
										Februar
									</MenuItem>
									<MenuItem key='month_2' value={2}>
										März
									</MenuItem>
									<MenuItem key='month_3' value={3}>
										April
									</MenuItem>
									<MenuItem key='month_4' value={4}>
										Mai
									</MenuItem>
									<MenuItem key='month_5' value={5}>
										Juni
									</MenuItem>
									<MenuItem key='month_6' value={6}>
										Juli
									</MenuItem>
									<MenuItem key='month_7' value={7}>
										August
									</MenuItem>
									<MenuItem key='month_8' value={8}>
										September
									</MenuItem>
									<MenuItem key='month_9' value={9}>
										Oktober
									</MenuItem>
									<MenuItem key='month_10' value={10}>
										November
									</MenuItem>
									<MenuItem key='month_11' value={11}>
										Dezember
									</MenuItem>
								</Select>
							</FormControl>
							<FormControl
								style={{width: '150px'}}
								variant='outlined'
							>
								<InputLabel>Jahr</InputLabel>
								<Select
									label='Jahr'
									value={this.state.selectedYear}
									onChange={(
										e: React.ChangeEvent<{
											name?: string;
											value: unknown;
										}>,
										c: ReactNode
									): void => {
										this.selectChanged(e, c, 'year');
									}}
								>
									{this.state.selectableYears.map(
										(year: number): ReactNode => (
											<MenuItem
												key={'year_' + year.toString()}
												value={year}
											>
												{year}
											</MenuItem>
										)
									)}
								</Select>
							</FormControl>
							<Button
								variant='contained'
								color='secondary'
								className={this.props.classes.reloadButton}
								onClick={this.loadEmployeesVacations.bind(this)}
							>
								Aktualisieren
							</Button>
						</div>
					</div>
					<Paper>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>Tag</TableCell>
									{this.state.timeTrackingMonths.map(
										(ttM) => (
											<TableCell key={ttM._id}>
												{ttM.userName}
											</TableCell>
										)
									)}
								</TableRow>
							</TableHead>
							<TableBody>
								{this.state.timeTrackingMonths.length > 0 &&
									this.state.timeTrackingMonths[0].days.map(
										(ttD, index) => (
											<TableRow
												key={ttD._id}
												className={
													ttD.isDayOff
														? this.props.classes
																.dayOff
														: ''
												}
											>
												<TableCell>
													{ttD.date.format(
														'dddd, DD.MM.YYYY'
													)}
												</TableCell>
												{this.state.timeTrackingMonths.map(
													(ttM) => (
														<TableCell
															key={`${ttD._id}-${ttM._id}`}
															className={
																ttM.days[index]
																	.isTooLittleBooked
																	? this.props
																			.classes
																			.negativeHours
																	: ''
															}
														>
															{printHours(
																ttM.days[index]
																	.bookedHours
															)}
															/
															{printHours(
																ttM.days[index]
																	.actualHours
															)}
														</TableCell>
													)
												)}
											</TableRow>
										)
									)}
							</TableBody>
							<TableFooter>
								<TableRow>
									<TableCell>Durchschnitt: </TableCell>
									{this.state.timeTrackingMonths.map(
										(ttM) => (
											<TableCell key={ttM._id}>
												{(
													Math.floor(
														ttM.averageBookedPercentage *
															10000
													) / 100
												).toString()}
												%
											</TableCell>
										)
									)}
								</TableRow>
							</TableFooter>
						</Table>
					</Paper>
				</div>
			</>
		);
	}
}

export default withStyles(bookedTimesOverviewStyles)(BookedTimesOverview);
